/**
 * Import the React library and the ChangePasswordComponent from the "./changePasswordComponent" file.
 * @module
 * @requires React
 * @requires ChangePasswordComponent
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useWindowDimension from "../../../components/hooks/useWindowDimension";
import { changePassword } from "../../../store/features/auth/authSlice";
import { showToast } from "../../../store/features/toast/toastSlice";
import Constant from "../../../util/constant";
import ChangePasswordComponent from "./changePasswordComponent";

const ChangePasswordContainer = (props) => {
  /**
   * Initializes the necessary state variables for the navigation, visibility, and busy status.
   * Also defines an array of error fields for a multi-step form validation.
   * @param {function} useNavigate - The hook for navigating to different routes.
   * @returns None
   */
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dimensions = useWindowDimension();
  const [show, setShow] = useState(true);
  const [showOld, setShowOld] = useState(true);
  const [showconfirm, setShowConfirm] = useState(true);
  const [oldPassValue, setOldPassValue] = useState("");
  const [busy, setBusy] = useState(false);
  const multiErrorFields = [
    { length: t("MINIMUM_8_CHARACTERS") },
    { hasSpecialChar: t("MINIMUM_1_SPECIAL_CHARACTER") },
    { hasNumbers: t("MINIMUM_1_NUMERIC_CHARACTER") },
    { hasUppercase: t("MINIMUM_1_UPPERCASE_CHARACTER") },
    { hasLowerCase: t("MINIMUM_1_LOWWERCASE_CHARACTER") },
    { samePassword: t("NEW_PASSWORD_CAN_NOT_BE_SAME_AS_OLD_PASSWORD") },
  ];
  const role = window.localStorage.getItem("role");

  /**
   * Toggles the value of the "show" state variable.
   * @returns None
   */
  const t_show = () => {
    setShow((prevShow) => !prevShow);
  };

  /**
   * Toggles the value of the "show" state variable.
   * @returns None
   */
  const t_show_old = () => {
    setShowOld((prevShow) => !prevShow);
  };

  /**
   * Toggles the value of `showConfirm` state variable.
   * @returns None
   */
  const t_show2 = () => {
    setShowConfirm((prevShowConfirm) => !prevShowConfirm);
  };

  /**
   * A reset form object that defines the structure and validation rules for the form fields.
   * @property {{name: string, validate: object}} newpassword - The new password field, with validation rules.
   * @property {{name: string}} password - The password field.
   */
  const resetForm = {
    oldpassword: {
      name: "oldpassword",
      validate: {
        required: {
          value: true,
          message: t("OLD_PASSWORD_REQUIRED"),
        },
        validate: {
          test: (value) => setOldPassValue(value),
        },
      },
    },
    newpassword: {
      name: "newpassword",
      validate: {
        required: {
          value: true,
          message: t("PLEASE_ENTER_A_PASSWORD"),
        },
        validate: {
          hasUppercase: (value) =>
            value && value.match(Constant.REGEX.UPPERCASE) !== null,
          hasLowerCase: (value) =>
            value && value.match(Constant.REGEX.LOWERCASE) !== null,
          hasNumbers: (value) =>
            value && value.match(Constant.REGEX.NUMBER) !== null,
          hasSpecialChar: (value) =>
            value && value.match(Constant.REGEX.SPECIALCHARACTERS) !== null,
          length: (value) => (value && value.length >= 8) || "",
          samePassword: (value) => value && value !== oldPassValue,
        },
      },
    },
    password: {
      name: "password",
    },
  };

  /**
   * Handles the submission of a form.
   * @param {{any}} value - The value submitted from the form.
   * @returns None
   */
  const onFormSubmit = async (value) => {
    setBusy(true);
    dispatch(changePassword(value)).then((resultAction) => {
      if (changePassword.fulfilled.match(resultAction)) {
        dispatch(
          showToast({
            show: false,
            message: resultAction?.payload?.message,
            type: "success",
          })
        );
        if (Constant.ROLES.ADMIN === role) {
          navigate("/club-sales-analysis?section=sales");
        } else if (Constant.ROLES.CLUB_MANAGER === role) {
          navigate("/club-sales-analysis?section=sales");
        } else if (Constant.ROLES.CLUB_EMPLOYEE === role) {
          navigate("/club-offline-sale");
        } else {
          navigate("/events-list");
        }
        setBusy(false);
      } else if (changePassword.rejected.match(resultAction)) {
        setBusy(false);
        dispatch(
          showToast({
            show: false,
            message: resultAction?.error?.message,
            type: "error",
          })
        );
      }
    });
  };
  const onClickCancel = async (value) => {
    navigate("/events-list");
  };
  return (
    <ChangePasswordComponent
      onFormSubmit={onFormSubmit}
      resetForm={resetForm}
      t_show2={t_show2}
      t_show={t_show}
      t_show_old={t_show_old}
      dimensions={dimensions}
      onClickCancel={onClickCancel}
      multiErrorFields={multiErrorFields}
      busy={busy}
      showconfirm={showconfirm}
      show={show}
      showOld={showOld}
    />
  );
};

export default ChangePasswordContainer;
